/**
 * Fleet domain helpers.
 *
 * Pure functions only — no Appwrite, no React. Availability, conflict
 * detection, compliance state and charging all live here so the Gantt, the
 * calendar, the booking form and the vehicle matcher agree on the answers
 * instead of each re-deriving them slightly differently.
 */

import {
  startOfDay,
  endOfDay,
  differenceInCalendarDays,
  differenceInMilliseconds
} from 'date-fns';

// =====================================================
// VOCABULARY
// =====================================================

export const VEHICLE_CATEGORIES = [
  { value: 'loan', label: 'Loan car', hint: 'Lent to customers while their car is in' },
  { value: 'company', label: 'Company vehicle', hint: 'Used internally — staff cars, tow trucks, parts runners' }
];

export const VEHICLE_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'maintenance', label: 'In service' },
  { value: 'off_road', label: 'Off road' },
  { value: 'retired', label: 'Retired' }
];

export const BOOKING_TYPES = [
  { value: 'loan', label: 'Loan', hint: 'Out with a customer' },
  { value: 'allocation', label: 'Allocation', hint: 'Assigned to someone internally' },
  { value: 'maintenance', label: 'Service / repair', hint: 'Off the road for work' }
];

export const BOOKING_STATUSES = [
  { value: 'reserved', label: 'Reserved' },
  { value: 'out', label: 'Out' },
  { value: 'returned', label: 'Returned' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_show', label: 'No show' }
];

export const TRANSMISSIONS = [
  { value: 'automatic', label: 'Automatic' },
  { value: 'manual', label: 'Manual' }
];

export const FUEL_TYPES = [
  { value: 'petrol', label: 'Petrol' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'plugin_hybrid', label: 'Plug-in hybrid' },
  { value: 'electric', label: 'Electric' }
];

export const BODY_TYPES = [
  { value: 'sedan', label: 'Sedan' },
  { value: 'suv', label: 'SUV' },
  { value: 'hatchback', label: 'Hatchback' },
  { value: 'ute', label: 'Ute' },
  { value: 'wagon', label: 'Wagon' },
  { value: 'van', label: 'Van' },
  { value: 'coupe', label: 'Coupe' },
  { value: 'people_mover', label: 'People mover' }
];

export const VEHICLE_SIZES = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' }
];

/** Statuses that still hold a vehicle — a cancelled booking frees the car. */
export const ACTIVE_BOOKING_STATUSES = ['reserved', 'out'];

/** Every booking type ties the vehicle up for its window. */
export const BLOCKING_BOOKING_TYPES = ['loan', 'allocation', 'maintenance'];

// Days of notice before an expiry is called "due soon".
export const EXPIRY_WARN_DAYS = 30;
export const SERVICE_WARN_DAYS = 14;
// Kilometres of notice for the odometer-based ones (RUC, service).
export const ODOMETER_WARN_KM = 500;
export const RUC_WARN_KM = 1000;

// =====================================================
// SMALL UTILITIES
// =====================================================

const toDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const labelFrom = (list, value) => list.find((o) => o.value === value)?.label || value || '';

export const vehicleCategoryLabel = (v) => labelFrom(VEHICLE_CATEGORIES, v);
export const vehicleStatusLabel = (v) => labelFrom(VEHICLE_STATUSES, v);
export const bookingTypeLabel = (v) => labelFrom(BOOKING_TYPES, v);
export const bookingStatusLabel = (v) => labelFrom(BOOKING_STATUSES, v);
export const transmissionLabel = (v) => labelFrom(TRANSMISSIONS, v);
export const fuelTypeLabel = (v) => labelFrom(FUEL_TYPES, v);
export const bodyTypeLabel = (v) => labelFrom(BODY_TYPES, v);

/** Id of the vehicle a booking is on, whether it came back expanded or flat. */
export function bookingVehicleId(booking) {
  const v = booking?.vehicle;
  if (!v) return null;
  return typeof v === 'string' ? v : v.$id || null;
}

/** Id of a related row that may be expanded, a string, or absent. */
export function relatedId(value) {
  if (!value) return null;
  return typeof value === 'string' ? value : value.$id || null;
}

/** "2019 Toyota Corolla" — falls back through the Other make/model fields. */
export function vehicleDescription(vehicle) {
  if (!vehicle) return '';
  const make = vehicle.make === 'Other' ? vehicle.otherMake : vehicle.make;
  const model = vehicle.model === 'Other' ? vehicle.otherModel : vehicle.model;
  return [vehicle.year, make, model].filter(Boolean).join(' ');
}

/** What to call a vehicle in a list: the shop's nickname, else make/model. */
export function vehicleLabel(vehicle) {
  if (!vehicle) return 'Unassigned';
  return vehicle.displayName?.trim() || vehicleDescription(vehicle) || vehicle.registration || 'Vehicle';
}

// =====================================================
// INTERVALS & CONFLICTS
// =====================================================

/**
 * A booking with no return date runs until someone ends it — the normal case
 * for a staff allocation. Representing that as a far-future end rather than a
 * null keeps every overlap, packing and clipping calculation working unchanged;
 * only charging and display need to know the difference.
 */
export const FAR_FUTURE = new Date(8640000000000000);

/** No return date set: the vehicle is out until further notice. */
export function isOpenEnded(booking) {
  return Boolean(booking) && !booking.scheduledEnd && !booking.actualEnd;
}

/**
 * The window a booking actually occupies.
 *
 * Once a car is collected the real handover time supersedes the plan, and a
 * car that is still out past its due date keeps occupying the vehicle — so an
 * overdue booking's end runs to at least `now`. Without that, an overdue car
 * looks free on the Gantt and gets double-booked.
 */
export function bookingInterval(booking, now = new Date()) {
  const scheduledStart = toDate(booking?.scheduledStart);
  const scheduledEnd = toDate(booking?.scheduledEnd);
  const actualStart = toDate(booking?.actualStart);
  const actualEnd = toDate(booking?.actualEnd);

  const start = actualStart || scheduledStart;
  const openEnded = !actualEnd && !scheduledEnd;
  let end = actualEnd || scheduledEnd || (openEnded ? FAR_FUTURE : null);

  if (!openEnded && !actualEnd && booking?.status === 'out' && end && now > end) {
    end = now;
  }

  return { start, end, openEnded };
}

/** Half-open overlap: a booking ending exactly when the next starts is fine. */
export function intervalsOverlap(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart < bEnd && bStart < aEnd;
}

/** Does this booking still hold its vehicle? */
export function isBookingActive(booking) {
  return (
    ACTIVE_BOOKING_STATUSES.includes(booking?.status || 'reserved') &&
    BLOCKING_BOOKING_TYPES.includes(booking?.type || 'loan')
  );
}

/**
 * Bookings that would clash with `candidate` on the same vehicle.
 * `candidate` may be an unsaved draft; a saved one is excluded from its own check.
 */
export function findConflicts(candidate, bookings, now = new Date()) {
  const vehicleId = bookingVehicleId(candidate);
  if (!vehicleId || !isBookingActive(candidate)) return [];

  const { start, end } = bookingInterval(candidate, now);
  if (!start || !end) return [];

  return bookings.filter((other) => {
    if (other.$id && candidate.$id && other.$id === candidate.$id) return false;
    if (bookingVehicleId(other) !== vehicleId) return false;
    if (!isBookingActive(other)) return false;
    const o = bookingInterval(other, now);
    return intervalsOverlap(start, end, o.start, o.end);
  });
}

/** Is this vehicle free for the whole window? */
export function isVehicleFree(vehicleId, start, end, bookings, { excludeBookingId, now = new Date() } = {}) {
  const from = toDate(start);
  const to = toDate(end);
  if (!from || !to) return false;

  return !bookings.some((b) => {
    if (excludeBookingId && b.$id === excludeBookingId) return false;
    if (bookingVehicleId(b) !== vehicleId) return false;
    if (!isBookingActive(b)) return false;
    const o = bookingInterval(b, now);
    return intervalsOverlap(from, to, o.start, o.end);
  });
}

/** Loans with no vehicle picked yet — the "needs a car" queue. */
export function unassignedBookings(bookings) {
  return bookings.filter(
    (b) =>
      ['loan', 'allocation'].includes(b.type || 'loan') &&
      !bookingVehicleId(b) &&
      ACTIVE_BOOKING_STATUSES.includes(b.status || 'reserved')
  );
}

/** Out, and past when it was due back. An open-ended booking is never overdue. */
export function isOverdue(booking, now = new Date()) {
  if (booking?.status !== 'out') return false;
  const due = toDate(booking?.scheduledEnd);
  return Boolean(due && now > due);
}

/** Reserved, and the pickup time has already passed. */
export function isLatePickup(booking, now = new Date()) {
  if (booking?.status !== 'reserved') return false;
  const start = toDate(booking?.scheduledStart);
  return Boolean(start && now > start);
}

/** What a vehicle is doing at a moment in time. */
export function vehicleStateAt(vehicle, bookings, at = new Date()) {
  if (vehicle?.status === 'retired') return 'retired';
  if (vehicle?.status === 'off_road') return 'off_road';

  const active = bookings.find((b) => {
    if (bookingVehicleId(b) !== vehicle?.$id) return false;
    if (!isBookingActive(b)) return false;
    const { start, end } = bookingInterval(b, at);
    return start && end && at >= start && at < end;
  });

  if (active) {
    if (active.type === 'maintenance') return 'maintenance';
    if (active.status !== 'out') return 'reserved';
    return active.type === 'allocation' ? 'allocated' : 'on_loan';
  }

  if (vehicle?.status === 'maintenance') return 'maintenance';
  return 'available';
}

export const VEHICLE_STATE_META = {
  available: { label: 'Available', tone: 'ok' },
  reserved: { label: 'Reserved', tone: 'info' },
  on_loan: { label: 'Out', tone: 'busy' },
  allocated: { label: 'Allocated', tone: 'busy' },
  maintenance: { label: 'In service', tone: 'warn' },
  off_road: { label: 'Off road', tone: 'bad' },
  retired: { label: 'Retired', tone: 'muted' }
};

// =====================================================
// MATCHING
// =====================================================

/**
 * Vehicles that are both free for the window and meet the requirements.
 *
 * `requirements` keys are all optional: transmission, fuelType, bodyType,
 * vehicleSize, minSeats, hasTowbar, minChildSeatAnchors, category.
 */
export function findAvailableVehicles(vehicles, bookings, { start, end, requirements = {}, excludeBookingId, now = new Date() } = {}) {
  const { transmission, fuelType, bodyType, vehicleSize, minSeats, hasTowbar, minChildSeatAnchors, category } =
    requirements;

  return vehicles.filter((v) => {
    if (v.status === 'retired' || v.status === 'off_road') return false;
    if (v.availableForLoan === false) return false;
    if (category && v.category !== category) return false;
    if (transmission && v.transmission !== transmission) return false;
    if (fuelType && v.fuelType !== fuelType) return false;
    if (bodyType && v.bodyType !== bodyType) return false;
    if (vehicleSize && v.vehicleSize !== vehicleSize) return false;
    if (minSeats && (v.seats || 0) < minSeats) return false;
    if (hasTowbar && !v.hasTowbar) return false;
    if (minChildSeatAnchors && (v.childSeatAnchors || 0) < minChildSeatAnchors) return false;
    // An end of `undefined` means open-ended: free from `start` onwards.
    if (start && !isVehicleFree(v.$id, start, end || FAR_FUTURE, bookings, { excludeBookingId, now })) return false;
    return true;
  });
}

// =====================================================
// COMPLIANCE
// =====================================================

const dateItem = (key, label, value, warnDays, now) => {
  const due = toDate(value);
  if (!due) return { key, label, kind: 'date', status: 'not_set', due: null, daysRemaining: null };
  const daysRemaining = differenceInCalendarDays(startOfDay(due), startOfDay(now));
  let status = 'ok';
  if (daysRemaining < 0) status = 'expired';
  else if (daysRemaining <= warnDays) status = 'due_soon';
  return { key, label, kind: 'date', status, due, daysRemaining };
};

const odometerItem = (key, label, target, current, warnKm) => {
  if (target === null || target === undefined || target === '') {
    return { key, label, kind: 'odometer', status: 'not_set', target: null, kmRemaining: null };
  }
  if (current === null || current === undefined || current === '') {
    return { key, label, kind: 'odometer', status: 'unknown', target, kmRemaining: null };
  }
  const kmRemaining = Number(target) - Number(current);
  let status = 'ok';
  if (kmRemaining < 0) status = 'expired';
  else if (kmRemaining <= warnKm) status = 'due_soon';
  return { key, label, kind: 'odometer', status, target: Number(target), kmRemaining };
};

/**
 * Every compliance obligation on a vehicle, in one shape the UI can loop over.
 * Service is reported twice — by date and by odometer — because whichever
 * comes first is what actually falls due.
 */
export function complianceItems(vehicle, now = new Date()) {
  if (!vehicle) return [];
  return [
    // One field covers both: a vehicle needs a WoF or a CoF, never both, and
    // which one applies is a property of the vehicle rather than of the date.
    dateItem('wof', 'WoF/CoF', vehicle.wofExpiryDate, EXPIRY_WARN_DAYS, now),
    dateItem('rego', 'Rego', vehicle.regoExpiryDate, EXPIRY_WARN_DAYS, now),
    dateItem('service', 'Service', vehicle.serviceDueDate, SERVICE_WARN_DAYS, now),
    odometerItem('serviceKm', 'Service (km)', vehicle.serviceDueOdometer, vehicle.odometer, ODOMETER_WARN_KM),
    odometerItem('ruc', 'RUC', vehicle.rucExpiryOdometer, vehicle.odometer, RUC_WARN_KM)
  ];
}

const STATUS_RANK = { expired: 3, due_soon: 2, unknown: 1, ok: 0, not_set: 0 };

/** The worst compliance state across a vehicle: expired > due_soon > ok. */
export function vehicleComplianceStatus(vehicle, now = new Date()) {
  if (vehicle?.remindersEnabled === false) return 'disabled';
  const items = complianceItems(vehicle, now);
  return items.reduce((worst, item) => (STATUS_RANK[item.status] > STATUS_RANK[worst] ? item.status : worst), 'ok');
}

/** Vehicles with something expired or falling due, worst first. */
export function complianceAlerts(vehicles, now = new Date()) {
  const alerts = [];
  vehicles.forEach((vehicle) => {
    if (vehicle.remindersEnabled === false || vehicle.status === 'retired') return;
    complianceItems(vehicle, now).forEach((item) => {
      if (item.status === 'expired' || item.status === 'due_soon') {
        alerts.push({ vehicle, ...item });
      }
    });
  });
  return alerts.sort((a, b) => {
    if (STATUS_RANK[b.status] !== STATUS_RANK[a.status]) return STATUS_RANK[b.status] - STATUS_RANK[a.status];
    const aKey = a.kind === 'date' ? a.daysRemaining : a.kmRemaining;
    const bKey = b.kind === 'date' ? b.daysRemaining : b.kmRemaining;
    return (aKey ?? 0) - (bKey ?? 0);
  });
}

/** "expired 3 days ago", "due in 12 days", "in 400 km". */
export function complianceSummary(item) {
  if (!item) return '';
  if (item.status === 'not_set') return 'not set';
  if (item.status === 'unknown') return 'odometer unknown';
  if (item.kind === 'date') {
    const d = item.daysRemaining;
    if (d < 0) return `expired ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} ago`;
    if (d === 0) return 'due today';
    return `due in ${d} day${d === 1 ? '' : 's'}`;
  }
  const km = item.kmRemaining;
  if (km < 0) return `overdue by ${Math.abs(km).toLocaleString()} km`;
  return `due in ${km.toLocaleString()} km`;
}

// =====================================================
// CHARGING
// =====================================================

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Chargeable days: 24-hour periods, rounded up, minimum one.
 * Matches how hire is normally billed — any part of a day is a day.
 */
export function chargeableDays(start, end) {
  const from = toDate(start);
  const to = toDate(end);
  if (!from || !to) return 0;
  const ms = differenceInMilliseconds(to, from);
  if (ms <= 0) return 1;
  return Math.max(1, Math.ceil(ms / MS_PER_DAY));
}

/**
 * What to charge for a booking. The admin fee is a one-off; the day rate runs
 * over the actual hire where it is known, otherwise the booked window. An
 * open-ended booking is charged to today — the running total so far.
 */
export function estimateCharge(booking, now = new Date()) {
  if (!booking || booking.chargeToCustomer === false) {
    return { days: 0, dailyRate: 0, adminFee: 0, dailyTotal: 0, total: 0, ongoing: false };
  }
  const { start, end, openEnded } = bookingInterval(booking, now);
  const days = chargeableDays(start, openEnded ? now : end);
  const dailyRate = Number(booking.dailyRate) || 0;
  const adminFee = Number(booking.adminFee) || 0;
  const dailyTotal = days * dailyRate;
  return { days, dailyRate, adminFee, dailyTotal, total: dailyTotal + adminFee, ongoing: openEnded };
}

/**
 * Share of available vehicle-days that were actually booked over a window.
 *
 * The honest answer to "do we need another courtesy car?" — a fleet sitting at
 * 40% has a scheduling problem, one at 95% has a capacity problem.
 */
export function utilisation(vehicles, bookings, from, to, now = new Date()) {
  // Normalise to whole days. Callers pass a range whose end is midnight on the
  // last day; measuring booked time against that while counting the day toward
  // capacity would under-report utilisation by up to a day in every window.
  const start = toDate(from) && startOfDay(toDate(from));
  const end = toDate(to) && endOfDay(toDate(to));
  if (!start || !end || end <= start) return { percent: 0, bookedDays: 0, capacityDays: 0 };

  const loanable = vehicles.filter((v) => v.status !== 'retired' && v.availableForLoan !== false);
  const windowDays = Math.max(1, differenceInCalendarDays(end, start) + 1);
  const capacityDays = loanable.length * windowDays;
  if (!capacityDays) return { percent: 0, bookedDays: 0, capacityDays: 0 };

  const loanableIds = new Set(loanable.map((v) => v.$id));
  let bookedMs = 0;

  bookings.forEach((b) => {
    // Time in the workshop is downtime, not use, so servicing is left out.
    if (!['loan', 'allocation'].includes(b.type || 'loan') || !isBookingActive(b)) return;
    const id = bookingVehicleId(b);
    if (!id || !loanableIds.has(id)) return;
    const o = bookingInterval(b, now);
    if (!o.start || !o.end) return;
    const clippedStart = o.start < start ? start : o.start;
    const clippedEnd = o.end > end ? end : o.end;
    if (clippedEnd > clippedStart) bookedMs += clippedEnd - clippedStart;
  });

  const bookedDays = bookedMs / MS_PER_DAY;
  return {
    percent: Math.min(100, Math.round((bookedDays / capacityDays) * 100)),
    bookedDays: Math.round(bookedDays),
    capacityDays
  };
}

export function formatCurrency(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('en-NZ', { style: 'currency', currency: 'NZD', minimumFractionDigits: 2 });
}

// =====================================================
// CALENDAR / GANTT GEOMETRY
// =====================================================

/**
 * Where a booking sits on a timeline running from `rangeStart` to `rangeEnd`,
 * as percentages, clipped to the visible window. `clippedStart`/`clippedEnd`
 * flag that the bar runs off the edge so the UI can show a continuation arrow.
 */
export function bookingPosition(booking, rangeStart, rangeEnd, now = new Date()) {
  const { start, end } = bookingInterval(booking, now);
  if (!start || !end) return null;

  const rs = toDate(rangeStart);
  const re = toDate(rangeEnd);
  const span = re - rs;
  if (span <= 0) return null;
  if (end <= rs || start >= re) return null;

  const visibleStart = start < rs ? rs : start;
  const visibleEnd = end > re ? re : end;

  return {
    left: ((visibleStart - rs) / span) * 100,
    width: Math.max(((visibleEnd - visibleStart) / span) * 100, 0.6),
    clippedStart: start < rs,
    clippedEnd: end > re
  };
}

/**
 * Pack a lane's bookings into non-overlapping sub-rows.
 *
 * A clash is the thing you most need to see, so overlapping bars stack instead
 * of hiding one another. Greedy first-fit over start-sorted bookings, which is
 * optimal for interval graphs and gives a stable layout frame to frame.
 */
export function packBookingRows(bookings, now = new Date()) {
  const sorted = [...bookings].sort((a, b) => {
    const aStart = bookingInterval(a, now).start;
    const bStart = bookingInterval(b, now).start;
    return (aStart?.getTime() || 0) - (bStart?.getTime() || 0);
  });

  const rows = [];
  sorted.forEach((booking) => {
    const { start, end } = bookingInterval(booking, now);
    if (!start || !end) return;
    const row = rows.find((existing) =>
      existing.every((other) => {
        const o = bookingInterval(other, now);
        return !intervalsOverlap(start, end, o.start, o.end);
      })
    );
    if (row) row.push(booking);
    else rows.push([booking]);
  });

  return rows;
}

/** Bookings touching a given calendar day, for the month view's day cells. */
export function bookingsOnDay(bookings, day, now = new Date()) {
  const from = startOfDay(day);
  const to = endOfDay(day);
  return bookings.filter((b) => {
    const { start, end } = bookingInterval(b, now);
    return intervalsOverlap(from, to, start, end);
  });
}

/**
 * Per-day availability counts for the month calendar — the "4 vehicles"
 * caption under each date in Panel Quote, but derived rather than stored.
 */
export function dailyAvailability(vehicles, bookings, days, now = new Date()) {
  const loanable = vehicles.filter(
    (v) => v.availableForLoan !== false && v.status !== 'retired' && v.status !== 'off_road'
  );

  return days.map((day) => {
    const from = startOfDay(day);
    const to = endOfDay(day);

    const busyIds = new Set();
    bookings.forEach((b) => {
      if (!isBookingActive(b)) return;
      const id = bookingVehicleId(b);
      if (!id) return;
      const o = bookingInterval(b, now);
      if (intervalsOverlap(from, to, o.start, o.end)) busyIds.add(id);
    });

    const available = loanable.filter((v) => !busyIds.has(v.$id)).length;
    return { day, total: loanable.length, available, busy: loanable.length - available };
  });
}
